import React, { useEffect, useState, useMemo } from "react";
import {
  Card,
  Grid,
  Table,
  Button,
  Icon,
  Input,
  Dropdown,
  Form,
  Progress,
  Label,
  Segment,
  Header,
  Checkbox,
  Message,
  Tab,
  Modal,
  TextArea,
  Statistic,
} from "semantic-ui-react";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import "./WasteBaselineModule.scss";

// Colores para el gráfico
const CHART_COLORS = [
  "#2563eb", "#0ea5e9", "#22c55e", "#f59e0b", "#ef4444",
  "#8b5cf6", "#14b8a6", "#f43f5e", "#84cc16", "#a855f7",
];

// Métodos de disposición por defecto (Higg FEM 2024)
const DEFAULT_METHODS = [
  { method: "Reciclaje", category: "Valorización" },
  { method: "Reuso interno", category: "Valorización" },
  { method: "Compostaje / Orgánicos", category: "Tratamiento" },
  { method: "Incineración con control", category: "Tratamiento" },
  { method: "Incineración sin control", category: "Disposición Final" },
  { method: "Relleno sanitario controlado", category: "Disposición Final" },
  { method: "Vertedero abierto / sin control", category: "Disposición Final" },
];

const UNITS = [
  { key: "kg", text: "kg", value: "kg" },
  { key: "ton", text: "ton", value: "ton" },
  { key: "m3", text: "m³", value: "m3" },
];

const STORAGE_KEY = "sustemia_waste_baseline_v1";

// Utilidades

// Los campos de texto libre (comentarios, método de cálculo, nombre de
// evidencia) los escribe cualquier usuario con acceso de edición, y terminan
// en un Excel/CSV que puede abrir otra persona (ej. un admin de la misma
// empresa). Si un valor empieza con =, +, - o @, Excel/LibreOffice lo puede
// interpretar como fórmula (o comando DDE en versiones viejas) en vez de
// texto plano — anteponer un apóstrofe fuerza a que se trate como texto.
function sanitizeForSpreadsheet(value) {
  const str = value === null || value === undefined ? "" : String(value);
  return /^[=+\-@]/.test(str) ? `'${str}` : str;
}

function calculatePercentage(quantity, total) {
  if (!total || total === 0 || !quantity) return 0;
  const p = (Number(quantity) / Number(total)) * 100;
  return Number.isFinite(p) ? p : 0;
}

function getYearList() {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let i = currentYear; i >= currentYear - 15; i--) {
    years.push({ key: i, text: i.toString(), value: i });
  }
  return years;
}

function formatNumber(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return "-";
  return x.toLocaleString();
}

function formatPercent(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return "0.00%";
  return x.toFixed(2) + "%";
}

// Crear fila vacía
function createEmptyRow(methodName = "") {
  return {
    id: Date.now() + Math.random(),
    method: methodName,
    hasBaseline: false,
    quantity: "",
    unit: "kg",
    baselineYear: new Date().getFullYear(),
    calculationMethod: "",
    verified: false,
    comments: "",
    evidenceName: "",
    evidenceUrl: "",
  };
}

export default function WasteBaselineModule({ site }) {
  // Estados
  const [loading, setLoading] = useState(false);
  const [totalWaste, setTotalWaste] = useState(0);
  const [totalUnit, setTotalUnit] = useState("kg");
  const [rows, setRows] = useState([]);
  const [generalComments, setGeneralComments] = useState("");
  const [newMethod, setNewMethod] = useState("");
  const [activeTab, setActiveTab] = useState(0);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingRow, setEditingRow] = useState(null);

  const currentYear = new Date().getFullYear();

  // Cargar datos desde localStorage (temporal hasta backend)
  useEffect(() => {
    loadData();
  }, [site]);

  const loadData = () => {
    // TODO: Reemplazar con llamada al backend
    // const response = await wasteBaselineController.getBaselineBySiteAndYear(accessToken, site, currentYear);

    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        setTotalWaste(parsed.totalWaste || 0);
        setTotalUnit(parsed.totalUnit || "kg");
        setRows(parsed.rows || []);
        setGeneralComments(parsed.generalComments || "");
      } catch (e) {
        console.error("Error al cargar datos:", e);
        initializeDefaults();
      }
    } else {
      initializeDefaults();
    }
  };

  const initializeDefaults = () => {
    setRows(DEFAULT_METHODS.map((m) => createEmptyRow(m.method)));
  };

  // Guardar datos
  const saveData = () => {
    setLoading(true);
    try {
      // TODO: Reemplazar con llamada al backend
      // const response = await wasteBaselineController.saveBaseline(accessToken, {
      //   site,
      //   year: currentYear,
      //   totalWaste,
      //   totalUnit,
      //   generalComments,
      //   methods: rows,
      // });

      const payload = { totalWaste, totalUnit, rows, generalComments };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
      toast.success("Datos guardados correctamente");
    } catch (error) {
      console.error("Error al guardar:", error);
      toast.error("Error al guardar los datos");
    } finally {
      setLoading(false);
    }
  };

  // Resetear datos
  const resetData = () => {
    if (!window.confirm("¿Está seguro de borrar todos los datos y volver a empezar?")) return;

    // TODO: Reemplazar con llamada al backend
    // await wasteBaselineController.deleteBaseline(accessToken, baselineId);

    localStorage.removeItem(STORAGE_KEY);
    setTotalWaste(0);
    setTotalUnit("kg");
    setGeneralComments("");
    initializeDefaults();
    toast.info("Datos reseteados");
  };

  // Calcular totales
  const totalPercentage = useMemo(() => {
    return rows.reduce(
      (acc, row) => acc + calculatePercentage(Number(row.quantity || 0), Number(totalWaste || 0)),
      0
    );
  }, [rows, totalWaste]);

  const hasWarning = totalPercentage > 0 && (totalPercentage < 99.5 || totalPercentage > 100.5);

  // Estadísticas
  const stats = useMemo(() => {
    const withBaseline = rows.filter((r) => r.hasBaseline && Number(r.quantity) > 0);
    const verified = withBaseline.filter((r) => r.verified);
    const totalQuantity = withBaseline.reduce((acc, r) => acc + Number(r.quantity || 0), 0);

    return {
      methodsWithBaseline: withBaseline.length,
      totalMethods: rows.length,
      verifiedCount: verified.length,
      totalQuantity,
    };
  }, [rows]);

  // Manejar cambios en filas
  const updateRow = (index, field, value) => {
    setRows((prev) => {
      const copy = [...prev];
      copy[index] = { ...copy[index], [field]: value };
      return copy;
    });
  };

  // Agregar nueva fila
  const addRow = () => {
    if (!newMethod.trim()) {
      toast.warning("Ingrese el nombre del método");
      return;
    }
    setRows((prev) => [...prev, createEmptyRow(newMethod.trim())]);
    setNewMethod("");
    toast.success("Método agregado");
  };

  // Eliminar fila
  const deleteRow = (index) => {
    if (!window.confirm("¿Eliminar este método?")) return;
    setRows((prev) => prev.filter((_, i) => i !== index));
  };

  // Abrir modal de edición
  const openEditModal = (row, index) => {
    setEditingRow({ ...row, index });
    setEditModalOpen(true);
  };

  // Guardar edición desde modal
  const saveEditModal = () => {
    if (editingRow) {
      const { index, ...rowData } = editingRow;
      setRows((prev) => {
        const copy = [...prev];
        copy[index] = rowData;
        return copy;
      });
      setEditModalOpen(false);
      setEditingRow(null);
      toast.success("Método actualizado");
    }
  };

  // Manejar archivo de evidencia
  const handleFileUpload = (index, file) => {
    if (!file) return;

    // TODO: Subir archivo al backend
    // const response = await fileController.upload(accessToken, file);
    // const url = response.url;

    const url = URL.createObjectURL(file);
    updateRow(index, "evidenceName", file.name);
    updateRow(index, "evidenceUrl", url);
  };

  // Exportar a Excel
  const exportToExcel = () => {
    const data = rows.map((r, idx) => ({
      "#": idx + 1,
      Método: sanitizeForSpreadsheet(r.method),
      "Tiene LB": r.hasBaseline ? "Sí" : "No",
      "Cantidad LB": r.quantity,
      Unidad: r.unit,
      "Año LB": r.baselineYear,
      "% del Total": formatPercent(calculatePercentage(r.quantity, totalWaste)),
      "Cómo se calculó": sanitizeForSpreadsheet(r.calculationMethod),
      Verificado: r.verified ? "Sí" : "No",
      Comentarios: sanitizeForSpreadsheet(r.comments),
      Evidencia: sanitizeForSpreadsheet(r.evidenceName),
    }));

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(data);
    XLSX.utils.book_append_sheet(wb, ws, "Métodos_Disposición");

    // Hoja de resumen
    const resumen = [
      ["Línea Base de Residuos - Higg FEM 2024"],
      [""],
      ["Total de residuos (año base)", totalWaste, totalUnit],
      ["Suma de porcentajes", formatPercent(totalPercentage)],
      ["Métodos con línea base", stats.methodsWithBaseline],
      ["Métodos verificados", stats.verifiedCount],
      ["Fecha de exportación", new Date().toLocaleString()],
      [""],
      ["Comentarios generales", sanitizeForSpreadsheet(generalComments)],
    ];
    const ws2 = XLSX.utils.aoa_to_sheet(resumen);
    XLSX.utils.book_append_sheet(wb, ws2, "Resumen");

    XLSX.writeFile(wb, `Baseline_Residuos_${new Date().toISOString().slice(0, 10)}.xlsx`);
    toast.success("Archivo Excel exportado");
  };

  // Exportar a CSV
  const exportToCSV = () => {
    const headers = [
      "Método", "Tiene_LB", "Cantidad_LB", "Unidad", "Año_LB",
      "%_del_Total", "Cómo_se_calculó", "Verificado", "Comentarios", "Evidencia"
    ];

    const csvRows = [
      headers.join(","),
      ...rows.map((r) => [
        `"${sanitizeForSpreadsheet(r.method)}"`,
        r.hasBaseline ? "Sí" : "No",
        r.quantity,
        r.unit,
        r.baselineYear,
        formatPercent(calculatePercentage(r.quantity, totalWaste)),
        `"${sanitizeForSpreadsheet((r.calculationMethod || "").replace(/"/g, '""'))}"`,
        r.verified ? "Sí" : "No",
        `"${sanitizeForSpreadsheet((r.comments || "").replace(/"/g, '""'))}"`,
        `"${sanitizeForSpreadsheet(r.evidenceName || "")}"`,
      ].join(","))
    ];

    const blob = new Blob([csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Baseline_Residuos_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    toast.success("Archivo CSV exportado");
  };

  // Datos para el gráfico
  const chartData = useMemo(() => {
    return rows
      .filter((r) => r.hasBaseline && Number(r.quantity) > 0)
      .map((r, idx) => ({
        name: r.method,
        value: calculatePercentage(Number(r.quantity || 0), Number(totalWaste || 0)),
        quantity: Number(r.quantity || 0),
        unit: r.unit,
        color: CHART_COLORS[idx % CHART_COLORS.length],
      }));
  }, [rows, totalWaste]);

  // Contenido de las pestañas
  const panes = [
    {
      menuItem: { key: "tabla", icon: "table", content: "Tabla de métodos" },
      render: () => (
        <Tab.Pane>
          <Segment>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "15px" }}>
              <Header as="h4">Métodos y líneas base</Header>
              <Button.Group size="small">
                <Button onClick={exportToCSV} icon labelPosition="left">
                  <Icon name="download" />
                  CSV
                </Button>
                <Button primary onClick={exportToExcel} icon labelPosition="left">
                  <Icon name="file excel" />
                  Excel
                </Button>
              </Button.Group>
            </div>

            <Table celled striped compact>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell width={3}>Método</Table.HeaderCell>
                  <Table.HeaderCell width={1} textAlign="center">¿Tiene LB?</Table.HeaderCell>
                  <Table.HeaderCell width={2}>Cantidad LB</Table.HeaderCell>
                  <Table.HeaderCell width={1}>Unidad</Table.HeaderCell>
                  <Table.HeaderCell width={1}>Año LB</Table.HeaderCell>
                  <Table.HeaderCell width={1} textAlign="right">% del Total</Table.HeaderCell>
                  <Table.HeaderCell width={1} textAlign="center">¿Verificado?</Table.HeaderCell>
                  <Table.HeaderCell width={2}>Acciones</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {rows.map((row, idx) => {
                  const pct = calculatePercentage(Number(row.quantity || 0), Number(totalWaste || 0));
                  return (
                    <Table.Row key={row.id || idx}>
                      <Table.Cell>
                        <Label circular size="tiny" style={{ marginRight: "8px" }}>
                          {idx + 1}
                        </Label>
                        {row.method}
                      </Table.Cell>
                      <Table.Cell textAlign="center">
                        <Checkbox
                          toggle
                          checked={row.hasBaseline}
                          onChange={(e, { checked }) => updateRow(idx, "hasBaseline", checked)}
                        />
                      </Table.Cell>
                      <Table.Cell>
                        <Input
                          type="number"
                          min="0"
                          value={row.quantity}
                          onChange={(e) => updateRow(idx, "quantity", e.target.value)}
                          placeholder="Ej: 255000"
                          fluid
                          size="small"
                        />
                      </Table.Cell>
                      <Table.Cell>
                        <Dropdown
                          selection
                          compact
                          options={UNITS}
                          value={row.unit}
                          onChange={(e, { value }) => updateRow(idx, "unit", value)}
                        />
                      </Table.Cell>
                      <Table.Cell>
                        <Dropdown
                          selection
                          compact
                          options={getYearList()}
                          value={row.baselineYear}
                          onChange={(e, { value }) => updateRow(idx, "baselineYear", value)}
                        />
                      </Table.Cell>
                      <Table.Cell textAlign="right">
                        <Label color={pct > 0 ? "blue" : "grey"} size="small">
                          {formatPercent(pct)}
                        </Label>
                      </Table.Cell>
                      <Table.Cell textAlign="center">
                        <Checkbox
                          toggle
                          checked={row.verified}
                          onChange={(e, { checked }) => updateRow(idx, "verified", checked)}
                        />
                      </Table.Cell>
                      <Table.Cell textAlign="center">
                        <Button.Group size="mini">
                          <Button icon="edit" onClick={() => openEditModal(row, idx)} />
                          <Button icon="trash" color="red" onClick={() => deleteRow(idx)} />
                        </Button.Group>
                      </Table.Cell>
                    </Table.Row>
                  );
                })}
              </Table.Body>
            </Table>

            {/* Agregar nuevo método */}
            <Segment secondary>
              <Grid>
                <Grid.Row>
                  <Grid.Column width={12}>
                    <Input
                      fluid
                      placeholder="Agregar método personalizado (ej: Co-procesamiento en hornos)"
                      value={newMethod}
                      onChange={(e) => setNewMethod(e.target.value)}
                      onKeyPress={(e) => e.key === "Enter" && addRow()}
                    />
                  </Grid.Column>
                  <Grid.Column width={4}>
                    <Button primary fluid onClick={addRow} icon labelPosition="left">
                      <Icon name="plus" />
                      Agregar
                    </Button>
                  </Grid.Column>
                </Grid.Row>
              </Grid>
            </Segment>

            {/* Comentarios generales */}
            <Form style={{ marginTop: "15px" }}>
              <Form.Field>
                <label>Comentarios generales</label>
                <TextArea
                  value={generalComments}
                  onChange={(e) => setGeneralComments(e.target.value)}
                  placeholder="Notas globales sobre el año base, cambios estructurales, etc."
                  rows={3}
                />
              </Form.Field>
            </Form>
          </Segment>
        </Tab.Pane>
      ),
    },
    {
      menuItem: { key: "mapa", icon: "chart pie", content: "Mapa de métodos" },
      render: () => (
        <Tab.Pane>
          <Segment>
            <Header as="h4">Distribución por método de disposición</Header>

            {chartData.length === 0 ? (
              <Message info>
                <Message.Header>Sin datos para el gráfico</Message.Header>
                <p>Active "Tiene LB" y agregue cantidades para ver la distribución.</p>
              </Message>
            ) : (
              <>
                <div style={{ width: "100%", height: 350 }}>
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie
                        data={chartData}
                        dataKey="value"
                        nameKey="name"
                        innerRadius={70}
                        outerRadius={120}
                        paddingAngle={2}
                        label={({ name, value }) => `${name}: ${value.toFixed(1)}%`}
                      >
                        {chartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(value, name, props) => [
                          `${formatPercent(value)} (${formatNumber(props.payload.quantity)} ${props.payload.unit})`,
                          name,
                        ]}
                      />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>

                {/* Cards por método */}
                <Card.Group itemsPerRow={3} stackable style={{ marginTop: "20px" }}>
                  {chartData.map((item, idx) => (
                    <Card key={idx}>
                      <Card.Content>
                        <Card.Header>
                          <span
                            style={{
                              display: "inline-block",
                              width: "12px",
                              height: "12px",
                              borderRadius: "50%",
                              backgroundColor: item.color,
                              marginRight: "8px",
                            }}
                          />
                          {item.name}
                        </Card.Header>
                        <Card.Description>
                          <Grid columns={2}>
                            <Grid.Row>
                              <Grid.Column>
                                <strong>% del total:</strong>
                              </Grid.Column>
                              <Grid.Column textAlign="right">
                                {formatPercent(item.value)}
                              </Grid.Column>
                            </Grid.Row>
                            <Grid.Row>
                              <Grid.Column>
                                <strong>Cantidad:</strong>
                              </Grid.Column>
                              <Grid.Column textAlign="right">
                                {formatNumber(item.quantity)} {item.unit}
                              </Grid.Column>
                            </Grid.Row>
                          </Grid>
                        </Card.Description>
                      </Card.Content>
                    </Card>
                  ))}
                </Card.Group>
              </>
            )}
          </Segment>
        </Tab.Pane>
      ),
    },
    {
      menuItem: { key: "guia", icon: "info circle", content: "Guía técnica" },
      render: () => (
        <Tab.Pane>
          <Segment>
            <Header as="h4">
              <Icon name="book" />
              Guía técnica (Higg FEM 2024)
            </Header>
            <Message info>
              <Message.Header>Definición</Message.Header>
              <p>
                La línea base es el punto de partida para medir mejoras. En métodos de disposición,
                representa el % del total de residuos que se gestiona por un método específico.
              </p>
            </Message>

            <Header as="h5">Recomendaciones</Header>
            <ul style={{ lineHeight: "1.8" }}>
              <li>Usar datos estables y un <strong>año calendario completo</strong>.</li>
              <li>Calcular: <code>% = (residuo por método / total residuos) × 100</code></li>
              <li>Revisar cambios estructurales (nuevas áreas, cambios de producto, etc.).</li>
              <li>Mantener la línea base fija si se usa contra objetivos.</li>
              <li>Verificar datos con manifiestos, registros de pesaje, auditorías o verificación FEM.</li>
            </ul>

            <Message warning>
              <Message.Header>Importante</Message.Header>
              <p>
                <strong>Do:</strong> Revisar fuentes, unidades, año base y detalles de cálculo.<br />
                <strong>Don't:</strong> Datos inexactos, periodos incompletos o estimaciones no verificables.
              </p>
            </Message>
          </Segment>
        </Tab.Pane>
      ),
    },
    {
      menuItem: { key: "ayuda", icon: "help circle", content: "Ayuda / Evidencias" },
      render: () => (
        <Tab.Pane>
          <Segment>
            <Grid columns={2} stackable>
              <Grid.Column>
                <Header as="h4">
                  <Icon name="upload" />
                  Subir evidencias
                </Header>
                <p>
                  Adjunte manifiestos, facturas, constancias de pesaje o reportes de verificación
                  en cada método desde el botón de editar.
                </p>
                <Message info size="small">
                  <strong>Tip:</strong> Renombre los archivos con el año base y método para trazabilidad.
                </Message>
              </Grid.Column>
              <Grid.Column>
                <Header as="h4">
                  <Icon name="check circle" />
                  Checklist de verificación
                </Header>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>¿El % total se acerca a 100%?</li>
                  <li>¿Las unidades son consistentes?</li>
                  <li>¿El año base corresponde a 12 meses completos?</li>
                  <li>¿Hubo cambios estructurales que invaliden el año seleccionado?</li>
                  <li>¿Existen registros trazables para cada cifra?</li>
                </ul>
              </Grid.Column>
            </Grid>
          </Segment>
        </Tab.Pane>
      ),
    },
  ];

  return (
    <div className="waste-baseline-module">
      {/* Header con totales */}
      <Segment>
        <Header as="h3">
          <Icon name="recycle" />
          Línea Base de Métodos de Disposición de Residuos
          <Header.Subheader>
            Alineado a Higg FEM 2024 (wstbaselinedisp). Completar por año calendario.
          </Header.Subheader>
        </Header>

        <Grid columns={3} stackable style={{ marginTop: "20px" }}>
          <Grid.Column>
            <Form>
              <Form.Field>
                <label>Total de residuos del año base</label>
                <Input
                  type="number"
                  min="0"
                  value={totalWaste}
                  onChange={(e) => setTotalWaste(Number(e.target.value))}
                  placeholder="Ej: 460555"
                  label={
                    <Dropdown
                      options={UNITS}
                      value={totalUnit}
                      onChange={(e, { value }) => setTotalUnit(value)}
                    />
                  }
                  labelPosition="right"
                />
              </Form.Field>
            </Form>
          </Grid.Column>
          <Grid.Column>
            <p style={{ marginBottom: "5px", fontWeight: "bold" }}>Suma de porcentajes</p>
            {hasWarning ? (
              <Message negative size="small">
                <Icon name="warning" />
                {formatPercent(totalPercentage)} - No cierra a 100%
              </Message>
            ) : (
              <Message positive size="small">
                <Icon name="check" />
                {formatPercent(totalPercentage)}
              </Message>
            )}
          </Grid.Column>
          <Grid.Column>
            <Button.Group fluid>
              <Button primary onClick={saveData} loading={loading} icon labelPosition="left">
                <Icon name="save" />
                Guardar
              </Button>
              <Button onClick={resetData} icon labelPosition="left">
                <Icon name="trash" />
                Reset
              </Button>
            </Button.Group>
          </Grid.Column>
        </Grid>
      </Segment>

      {/* Estadísticas */}
      <Grid columns={4} stackable style={{ marginTop: "20px" }}>
        <Grid.Column>
          <Card fluid color="blue">
            <Card.Content textAlign="center">
              <Statistic size="small" color="blue">
                <Statistic.Value>{stats.methodsWithBaseline}</Statistic.Value>
                <Statistic.Label>Métodos con LB</Statistic.Label>
              </Statistic>
            </Card.Content>
          </Card>
        </Grid.Column>
        <Grid.Column>
          <Card fluid color="green">
            <Card.Content textAlign="center">
              <Statistic size="small" color="green">
                <Statistic.Value>{stats.verifiedCount}</Statistic.Value>
                <Statistic.Label>Verificados</Statistic.Label>
              </Statistic>
            </Card.Content>
          </Card>
        </Grid.Column>
        <Grid.Column>
          <Card fluid color="orange">
            <Card.Content textAlign="center">
              <Statistic size="small" color="orange">
                <Statistic.Value>{formatNumber(stats.totalQuantity)}</Statistic.Value>
                <Statistic.Label>Cantidad Total ({totalUnit})</Statistic.Label>
              </Statistic>
            </Card.Content>
          </Card>
        </Grid.Column>
        <Grid.Column>
          <Card fluid color="purple">
            <Card.Content textAlign="center">
              <Statistic size="small" color="purple">
                <Statistic.Value>{stats.totalMethods}</Statistic.Value>
                <Statistic.Label>Total Métodos</Statistic.Label>
              </Statistic>
            </Card.Content>
          </Card>
        </Grid.Column>
      </Grid>

      {/* Pestañas */}
      <Tab
        panes={panes}
        activeIndex={activeTab}
        onTabChange={(e, { activeIndex }) => setActiveTab(activeIndex)}
        style={{ marginTop: "20px" }}
      />

      {/* Modal de edición */}
      <Modal open={editModalOpen} onClose={() => setEditModalOpen(false)} size="small">
        <Modal.Header>Editar Método: {editingRow?.method}</Modal.Header>
        <Modal.Content>
          {editingRow && (
            <Form>
              <Form.Group widths="equal">
                <Form.Field>
                  <label>Cantidad</label>
                  <Input
                    type="number"
                    min="0"
                    value={editingRow.quantity}
                    onChange={(e) => setEditingRow({ ...editingRow, quantity: e.target.value })}
                  />
                </Form.Field>
                <Form.Field>
                  <label>Unidad</label>
                  <Dropdown
                    selection
                    options={UNITS}
                    value={editingRow.unit}
                    onChange={(e, { value }) => setEditingRow({ ...editingRow, unit: value })}
                  />
                </Form.Field>
                <Form.Field>
                  <label>Año LB</label>
                  <Dropdown
                    selection
                    options={getYearList()}
                    value={editingRow.baselineYear}
                    onChange={(e, { value }) => setEditingRow({ ...editingRow, baselineYear: value })}
                  />
                </Form.Field>
              </Form.Group>

              <Form.Field>
                <label>Cómo se calculó</label>
                <TextArea
                  value={editingRow.calculationMethod}
                  onChange={(e) => setEditingRow({ ...editingRow, calculationMethod: e.target.value })}
                  placeholder="Manifiestos, facturas, registros de pesaje. Fórmula: cantidad método / total."
                  rows={3}
                />
              </Form.Field>

              <Form.Field>
                <label>Comentarios</label>
                <TextArea
                  value={editingRow.comments}
                  onChange={(e) => setEditingRow({ ...editingRow, comments: e.target.value })}
                  placeholder="Observaciones y aclaraciones relevantes."
                  rows={3}
                />
              </Form.Field>

              <Form.Group widths="equal">
                <Form.Field>
                  <label>¿Tiene Línea Base?</label>
                  <Checkbox
                    toggle
                    checked={editingRow.hasBaseline}
                    onChange={(e, { checked }) => setEditingRow({ ...editingRow, hasBaseline: checked })}
                    label={editingRow.hasBaseline ? "Sí" : "No"}
                  />
                </Form.Field>
                <Form.Field>
                  <label>¿Verificado?</label>
                  <Checkbox
                    toggle
                    checked={editingRow.verified}
                    onChange={(e, { checked }) => setEditingRow({ ...editingRow, verified: checked })}
                    label={editingRow.verified ? "Sí" : "No"}
                  />
                </Form.Field>
              </Form.Group>

              <Form.Field>
                <label>Evidencia</label>
                <Input
                  type="file"
                  accept="application/pdf,image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      const url = URL.createObjectURL(file);
                      setEditingRow({
                        ...editingRow,
                        evidenceName: file.name,
                        evidenceUrl: url,
                      });
                    }
                  }}
                />
                {editingRow.evidenceName && (
                  <p style={{ marginTop: "5px" }}>
                    <Icon name="file" />
                    <a href={editingRow.evidenceUrl} target="_blank" rel="noreferrer">
                      {editingRow.evidenceName}
                    </a>
                  </p>
                )}
              </Form.Field>
            </Form>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setEditModalOpen(false)}>Cancelar</Button>
          <Button primary onClick={saveEditModal}>
            <Icon name="save" />
            Guardar
          </Button>
        </Modal.Actions>
      </Modal>
    </div>
  );
}
